/**
 * Keyboard shortcuts used throughout the application
 * Format: "meta+shift+f" where:
 * - meta = Cmd on Mac, Ctrl on Windows/Linux
 * - shift, alt, ctrl can be combined
 * - Key is the main key (e.g., 'f', ',', 'k')
 */

// Helper to detect Mac platform, safe for SSR
const getIsMac = () => {
  if (typeof navigator === "undefined") return true; // Default to Mac for SSR
  return navigator.platform.toUpperCase().indexOf("MAC") >= 0;
};

export const KEYBOARD_SHORTCUTS = {
  FILTER: "meta+shift+f",
  SETTINGS: "meta+,",
  COMMAND_BAR: "meta+k",
  SELECT_ALL: "meta+a",
  ROW_START: "meta+ArrowLeft",
  ROW_END: "meta+ArrowRight",
  COLUMN_TOP: "meta+ArrowUp",
  COLUMN_BOTTOM: "meta+ArrowDown",
  OPEN_ENTITY_MODAL: "Enter",
  SAVE_ALL_EDITS: "meta+Enter",
  PROMOTE_CELLS_TO_ROWS: "shift+ ",
} as const;

export function matchesShortcut(event: KeyboardEvent, shortcut: string): boolean {
  const isMac = getIsMac();

  // Parse the shortcut string
  const parts = shortcut.toLowerCase().split("+");
  const key = parts[parts.length - 1]; // Last part is always the key

  // Check modifiers
  const requiresMeta = parts.includes("meta");
  const requiresShift = parts.includes("shift");
  const requiresAlt = parts.includes("alt");
  const requiresCtrl = parts.includes("ctrl");

  // Check if meta/ctrl is pressed
  const metaKey = isMac ? event.metaKey : event.ctrlKey;

  // If meta is required, check metaKey
  // If ctrl is required (not meta), check event.ctrlKey directly
  const metaPressed = requiresMeta ? metaKey : true;
  const ctrlPressed = requiresCtrl ? event.ctrlKey : true;

  return (
    event.key.toLowerCase() === key &&
    metaPressed &&
    ctrlPressed &&
    event.shiftKey === requiresShift &&
    event.altKey === requiresAlt
  );
}

/**
 * Get the display string for a shortcut
 */
export function getShortcutDisplay(shortcut: string): string {
  const isMac = getIsMac();
  const parts = shortcut.toLowerCase().split("+");

  const displayParts: string[] = [];

  // Convert meta to platform-specific symbol
  if (parts.includes("meta")) {
    displayParts.push(isMac ? "⌘" : "Ctrl");
  }

  // Add other modifiers
  if (parts.includes("shift")) {
    displayParts.push("⇧");
  }
  if (parts.includes("alt")) {
    displayParts.push("⌥");
  }
  if (parts.includes("ctrl")) {
    displayParts.push("⌃");
  }

  // Add the key (last part)
  const key = parts[parts.length - 1];
  displayParts.push(key.toUpperCase());

  return displayParts.join("");
}
